/**
 * Cache for signed URLs to avoid repeated API calls.
 * Signed URLs typically have an expiration time, so we cache them with a TTL.
 *
 * Note: This is a client-side (browser) in-memory cache. Redis or other server-side
 * caching solutions are not applicable here since this code runs in the user's browser.
 * The CacheManager in this codebase is also an in-memory Map-based cache.
 * This implementation includes TTL support appropriate for signed URLs which expire.
 */

interface CacheEntry {
   url: string;
   expiresAt: number; // timestamp in milliseconds
}

class SignedUrlCache {
   private cache: Map<string, CacheEntry> = new Map();
   // Default TTL: 50 minutes (assuming signed URLs are valid for 1 hour, with 10 min buffer)
   private readonly defaultTTL = 50 * 60 * 1000;

   /**
    * Get a cached signed URL if it exists and hasn't expired
    */
   get(attachmentId: string): string | null {
      const entry = this.cache.get(attachmentId);
      if (!entry) {
         return null;
      }

      // Check if expired
      if (Date.now() >= entry.expiresAt) {
         this.cache.delete(attachmentId);
         return null;
      }

      return entry.url;
   }

   /**
    * Store a signed URL in the cache
    */
   set(attachmentId: string, signedUrl: string, ttlMs?: number): void {
      const expiresAt = Date.now() + (ttlMs ?? this.defaultTTL);
      this.cache.set(attachmentId, {
         url: signedUrl,
         expiresAt,
      });
   }

   /**
    * Clear a specific entry from the cache
    */
   delete(attachmentId: string): void {
      this.cache.delete(attachmentId);
   }

   /**
    * Clear all cached entries
    */
   clear(): void {
      this.cache.clear();
   }

   /**
    * Remove expired entries (call periodically to prevent memory leaks)
    */
   cleanup(): void {
      const now = Date.now();
      for (const [key, entry] of this.cache.entries()) {
         if (now >= entry.expiresAt) {
            this.cache.delete(key);
         }
      }
   }
}

// Singleton instance
export const signedUrlCache = new SignedUrlCache();

// Run cleanup every 10 minutes
if (typeof window !== "undefined") {
   setInterval(() => {
      signedUrlCache.cleanup();
   }, 10 * 60 * 1000);
}
